/** @file Name validation: ASCII identifiers; animations may carry one @DIR suffix. */

const DIRS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
export type Dir = (typeof DIRS)[number];

export type NameKind =
  | 'project' | 'bone' | 'slot' | 'attachment' | 'skin' | 'animation'
  | 'event' | 'ik' | 'socket' | 'image' | 'reference' | 'token';

const NAME_MAX = 64;
const NAME = `[A-Za-z0-9][A-Za-z0-9_-]{0,${NAME_MAX - 1}}`;
const ANIMATION = `${NAME}(@(${DIRS.join('|')}))?`;

export const names = {
  /** Every named entity: `^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$` (at most 64 characters). */
  RE: new RegExp(`^${NAME}$`),
  /** Animation names: a plain name, optionally followed by exactly one `@<DIR>` suffix. */
  ANIMATION_RE: new RegExp(`^${ANIMATION}$`),
  /** `mixDefaults` keys: `<animation>><animation>`. */
  MIX_PAIR_RE: new RegExp(`^${ANIMATION}>${ANIMATION}$`),
  /** Longest allowed name, in characters (CR-001, contracts-v2). */
  MAX: NAME_MAX,
  /** The eight compass directions used by direction sets. */
  DIRS,
  /** True when `name` satisfies the rule for `kind`. */
  isValid(kind: NameKind, name: string): boolean {
    return kind === 'animation' ? names.ANIMATION_RE.test(name) : names.RE.test(name);
  },
  /** Splits `walk@SE` into `{ base: 'walk', dir: 'SE' }`; a plain name has `dir: null`. */
  splitAnimation(name: string): { base: string; dir: Dir | null } {
    const at = name.indexOf('@');
    if (at < 0) return { base: name, dir: null };
    return { base: name.slice(0, at), dir: name.slice(at + 1) as Dir };
  },
};
